import { useMemo } from 'react';
import { List } from 'immutable';
import { useDispatch } from 'react-redux';

import { LABEL_NAME_MAX_LENGTH } from '../../../../common/labels/labelConstants';
import { updateLabel } from '../../../../common/labels/labelActions';
import { ImMNElement } from '../../../../common/elements/elementModelTypes';
import { removeLabelFromElements } from '../../labels/elementLabelActions';
import { deselectAllElements } from '../../selection/selectionActions';
import type { ElementChipCallbackSet } from './useElementChipCallbacks';

export const ELEMENT_LABEL_CHIP_MAX_LENGTH = LABEL_NAME_MAX_LENGTH;

/**
 * Provides label-specific mutation callbacks for generic element chips.
 */
export const useLabelElementChipCallbacks = ({
    element,
    canModifyLabels,
}: {
    element: ImMNElement;
    canModifyLabels: boolean;
}): ElementChipCallbackSet => {
    const dispatch = useDispatch();

    return useMemo(
        () => (chip) => {
            if (chip.type !== 'label') return {};

            const labelId = chip.label._id;
            const removeLabel = () => {
                if (!canModifyLabels) return;

                dispatch(removeLabelFromElements(List([element]), labelId));
                dispatch(deselectAllElements());
            };

            return {
                canModify: canModifyLabels,
                onCommitEdit: (name: string) => {
                    if (!canModifyLabels) return;

                    dispatch(updateLabel(labelId, { name }));
                },
                onEmptyBackspace: removeLabel,
                onDeleteKeyDown: (event) => {
                    if (!canModifyLabels) return;

                    event.preventDefault();
                    event.stopPropagation();
                    removeLabel();
                },
            };
        },
        [canModifyLabels, dispatch, element],
    );
};
